import React, { useEffect, useState, useMemo } from 'react';
import { useStore } from '../store';
import { workload as workloadApi } from '../api';
import Avatar from '../components/UI/Avatar';
import { STATUS, PRIORITY, TASK_TYPE, fmt } from '../utils';
import {
  AlertTriangle, Clock, CheckCircle2, Flame, ChevronDown, ChevronRight,
  Filter, RefreshCw, BarChart3, Calendar
} from 'lucide-react';

const WORKLOAD_WEIGHTS = {
  inProgress: 1.0,
  todo: 0.7,
  overdue: 1.5,
  high: 0.5,
  critical: 1.0,
};

const WORKLOAD_THRESHOLD = 10;

function calcWorkloadScore(entry) {
  const { inProgress = 0, overdue = 0, byStatus = {}, byPriority = {} } = entry;
  const todo = byStatus.todo || 0;
  const high = byPriority.high || 0;
  const critical = byPriority.critical || 0;

  return (
    WORKLOAD_WEIGHTS.inProgress * inProgress +
    WORKLOAD_WEIGHTS.todo * todo +
    WORKLOAD_WEIGHTS.overdue * overdue +
    WORKLOAD_WEIGHTS.high * high +
    WORKLOAD_WEIGHTS.critical * critical
  );
}

function getWorkloadLevel(score, threshold = WORKLOAD_THRESHOLD) {
  const pct = threshold > 0 ? (score / threshold) * 100 : 0;
  if (pct > 100) return { level: 'overloaded', label: 'перегружен', pct };
  if (pct > 80) return { level: 'high', label: 'высокая', pct };
  return { level: 'normal', label: 'нормально', pct };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function LoadBar({ value, max, color = '#4f63ff' }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden w-full">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function HeatDot({ count, max }) {
  const intensity = max > 0 ? count / max : 0;
  const bg = intensity === 0
    ? 'bg-gray-100 dark:bg-gray-800'
    : intensity < 0.33
      ? 'bg-brand-200 dark:bg-brand-900'
      : intensity < 0.66
        ? 'bg-brand-400 dark:bg-brand-600'
        : 'bg-brand-600 dark:bg-brand-400';
  return (
    <div className={`w-5 h-5 rounded-sm ${bg} flex items-center justify-center text-[9px] font-bold text-white`}
      title={`${count} задач`}>
      {count > 0 ? count : ''}
    </div>
  );
}

// ── Member row (expandable) ──────────────────────────────────────────────────

function MemberRow({ entry, onTaskClick }) {
  const [open, setOpen] = useState(false);
  const { user, tasks, inProgress, overdue, totalLoggedMinutes, totalEstimatedMinutes, byPriority, byStatus } = entry;

  const score = calcWorkloadScore(entry);
  const workloadLevel = getWorkloadLevel(score);
  const hasOverdue = overdue > 0;

  const loadLabelCls = workloadLevel.level === 'overloaded'
    ? 'text-orange-500'
    : workloadLevel.level === 'high'
      ? 'text-amber-500'
      : 'text-gray-600 dark:text-gray-300';

  const loadColor = hasOverdue
    ? '#ef4444'
    : workloadLevel.level === 'overloaded'
      ? '#f97316'
      : workloadLevel.level === 'high'
        ? '#f59e0b'
        : '#4f63ff';

  return (
    <div className={`card mb-3 overflow-hidden transition-shadow ${open ? 'shadow-md' : ''}`}>
      {/* Header row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <button className="p-0.5 rounded text-gray-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>

        <Avatar name={user.name} color={user.color} src={user.avatar} size="md" />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{user.name}</span>
            {hasOverdue && (
              <span className="flex items-center gap-0.5 text-[11px] text-red-500 bg-red-50 dark:bg-red-950/50 px-1.5 py-0.5 rounded-full font-medium">
                <AlertTriangle className="w-3 h-3" /> {overdue} просрочено
              </span>
            )}
            {workloadLevel.level === 'overloaded' && (
              <span className="flex items-center gap-0.5 text-[11px] text-orange-500 bg-orange-50 dark:bg-orange-950/50 px-1.5 py-0.5 rounded-full font-medium">
                <Flame className="w-3 h-3" />перегружен
              </span>
            )}
            {workloadLevel.level === 'high' && (
              <span className="flex items-center gap-0.5 text-[11px] text-amber-500 bg-amber-50 dark:bg-amber-950/50 px-1.5 py-0.5 rounded-full font-medium">
                высокая
              </span>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{user.email}</p>
        </div>

        {/* Stats chips */}
        <div className="hidden sm:flex items-center gap-3 mr-4">
          <Chip icon={<CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />} label={`${byStatus.done || 0} готово`} />
          <Chip icon={<Clock className="w-3.5 h-3.5 text-amber-500" />} label={`${inProgress} в работе`} />
          <Chip icon={<BarChart3 className="w-3.5 h-3.5 text-brand-500" />} label={fmt.time(totalLoggedMinutes)} />
        </div>

        {/* Load bar */}
        <div className="w-32 hidden md:block">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>Загрузка</span>
            <span className={`font-medium ${loadLabelCls}`}>
              {Math.round(workloadLevel.pct)}%
            </span>
          </div>
          <LoadBar value={score} max={WORKLOAD_THRESHOLD} color={loadColor} />
        </div>
      </div>

      {/* Expanded task list */}
      {open && (
        <div className="border-t border-gray-100 dark:border-gray-800 animate-slide-in">
          {/* Priority breakdown */}
          <div className="flex gap-4 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
            {Object.entries(byPriority).filter(([, v]) => v > 0).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1 text-xs text-gray-500">
                {PRIORITY[k]?.icon} {v}
              </span>
            ))}
            <span className="ml-auto text-xs text-gray-400 flex items-center gap-1">
              score: {score.toFixed(1)} / {WORKLOAD_THRESHOLD}
            </span>
            {totalEstimatedMinutes > 0 && (
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {fmt.time(totalLoggedMinutes)} / {fmt.time(totalEstimatedMinutes)} оценка
              </span>
            )}
          </div>

          {tasks.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">Нет активных задач</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-50 dark:border-gray-800">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-400">Задача</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-28">Статус</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-10">!</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-28">Срок</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-gray-400 w-24">Проект</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
                {tasks.map(task => {
                  const s = STATUS[task.status];
                  const p = PRIORITY[task.priority];
                  const due = fmt.dueBadge(task.dueDate);
                  const isOverdueVisual = due?.cls?.includes('red') && task.status !== 'done' && task.status !== 'review';
                  return (
                    <tr key={task._id}
                      onClick={() => onTaskClick(task._id)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40 cursor-pointer transition-colors">
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs">{TASK_TYPE[task.type]?.icon}</span>
                          <span className="font-medium hover:text-brand-600 dark:hover:text-brand-400 transition-colors line-clamp-1">{task.title}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="tag text-xs" style={{ background: s?.color + '20', color: s?.color }}>
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: s?.color }} />
                          {s?.label}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-sm">{p?.icon}</td>
                      <td className="px-3 py-2.5">
                        {due && (
                          <span className={`tag text-[11px] ${isOverdueVisual ? due.cls : 'text-gray-500 bg-gray-100 dark:bg-gray-800'}`}>
                            {due.label}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        {task.project && (
                          <span className="flex items-center gap-1 text-xs text-gray-500">
                            <span>{task.project.icon || '📋'}</span>
                            <span className="truncate max-w-[80px]">{task.project.name}</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ icon, label }) {
  return (
    <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
      {icon}{label}
    </span>
  );
}

// ── Deadline calendar strip ─────────────────────────────────────────────────

function DeadlineStrip({ workload }) {
  // Collect all tasks with due dates from next14 days
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today); d.setDate(d.getDate() + i);
    return d;
  });

  const tasksByDay = useMemo(() => {
    const map = {};
    days.forEach(d => { map[d.toDateString()] = []; });
    workload.forEach(({ user, tasks }) => {
      tasks.forEach(task => {
        if (!task.dueDate) return;
        const d = new Date(task.dueDate); d.setHours(0, 0, 0, 0);
        const key = d.toDateString();
        if (map[key] !== undefined) map[key].push({ ...task, _assignee: user });
      });
    });
    return map;
  }, [workload]);

  const maxPerDay = Math.max(...days.map(d => tasksByDay[d.toDateString()]?.length || 0), 1);

  return (
    <div className="card p-4 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="w-4 h-4 text-brand-500" />
        <h3 className="font-semibold text-sm">Дедлайны — ближайшие14 дней</h3>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1">
        {days.map(day => {
          const key = day.toDateString();
          const count = tasksByDay[key]?.length || 0;
          const isToday = day.toDateString() === today.toDateString();
          const isWeekend = day.getDay() === 0 || day.getDay() === 6;
          return (
            <div key={key} className="flex flex-col items-center gap-1 flex-shrink-0 w-10">
              <span className={`text-[10px] font-medium ${isToday ? 'text-brand-600' : isWeekend ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400'}`}>
                {day.toLocaleDateString('ru', { weekday: 'short' })}
              </span>
              <span className={`text-[11px] font-bold ${isToday ? 'text-brand-600' : 'text-gray-500'}`}>
                {day.getDate()}
              </span>
              <HeatDot count={count} max={maxPerDay} />
              {count > 0 && (
                <span className="text-[9px] text-gray-400">{count}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function WorkloadPage() {
  const { projects, openTask } = useStore();
  const [workload, setWorkload] = useState([]);
  const [loading, setLoading] = useState(false);
  const [projectFilter, setProjectFilter] = useState('');
  const [sortBy, setSortBy] = useState('overdue'); // overdue | tasks | time

  const fetchWorkload = async () => {
    setLoading(true);
    try {
      const params = projectFilter ? { projectId: projectFilter } : {};
      const data = await workloadApi.list(params);
      setWorkload(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchWorkload(); }, [projectFilter]);

  const sorted = useMemo(() => {
    return [...workload].sort((a, b) => {
      if (sortBy === 'overdue') {
        if (b.overdue !== a.overdue) return b.overdue - a.overdue;
        return calcWorkloadScore(b) - calcWorkloadScore(a);
      }
      if (sortBy === 'tasks') return b.totalTasks - a.totalTasks;
      if (sortBy === 'time') return b.totalLoggedMinutes - a.totalLoggedMinutes;
      return0;
    });
  }, [workload, sortBy]);

  const totalActive = workload.reduce((s, w) => s + w.totalTasks, 0);
  const totalOverdue = workload.reduce((s, w) => s + w.overdue, 0);
  const totalLogged = workload.reduce((s, w) => s + w.totalLoggedMinutes, 0);
  const totalInProgress = workload.reduce((s, w) => s + w.inProgress, 0);

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold">Загруженность команды</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {workload.length} сотрудников · {totalActive} активных задач
            </p>
          </div>
          <button onClick={fetchWorkload} disabled={loading}
            className="btn-secondary py-1.5">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Активных задач', value: totalActive, icon: '📋', color: 'text-brand-600' },
            { label: 'В работе', value: totalInProgress, icon: '⚡', color: 'text-amber-600' },
            { label: 'Просрочено', value: totalOverdue, icon: '🔴', color: 'text-red-600' },
            { label: 'Залогировано', value: fmt.time(totalLogged), icon: '⏱', color: 'text-emerald-600' },
          ].map(card => (
            <div key={card.label} className="card px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-base">{card.icon}</span>
                <span className="text-xs text-gray-400">{card.label}</span>
              </div>
              <div className={`text-xl font-bold ${card.color}`}>{card.value}</div>
            </div>
          ))}
        </div>

        {/* Deadline calendar */}
        {workload.length > 0 && <DeadlineStrip workload={workload} />}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <Filter className="w-3.5 h-3.5" />
          </div>
          <select className="input w-auto text-sm" value={projectFilter}
            onChange={e => setProjectFilter(e.target.value)}>
            <option value="">Все проекты</option>
            {projects.map(p => <option key={p._id} value={p._id}>{p.icon} {p.name}</option>)}
          </select>
          <select className="input w-auto text-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
            <option value="overdue">По просрочке</option>
            <option value="tasks">По кол-ву задач</option>
            <option value="time">По времени</option>
          </select>
          <span className="text-xs text-gray-400 ml-auto">{sorted.length} сотрудников</span>
        </div>

        {/* Members list */}
        {loading && workload.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-50" />
            Загрузка данных...
          </div>
        ) : sorted.length === 0 ? (
          <div className="card p-12 text-center">
            <p className="text-gray-400 text-sm">Нет активных задач с исполнителями</p>
            <p className="text-gray-300 dark:text-gray-600 text-xs mt-1">
              Назначьте сотрудников на задачи, чтобы видеть загруженность
            </p>
          </div>
        ) : (
          sorted.map(entry => (
            <MemberRow key={entry.user._id} entry={entry} onTaskClick={openTask} />
          ))
        )}
      </div>
    </div>
  );
}
